import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { handleApi } from "../../../utils/authentication";
import { toast } from "react-toastify";
import LoadingSpinner from "../../../components/client/LoadingSpinner";
import CampaignCard from "./components/CampaignCard";
import CampaignViewEdit from "./components/CampaignViewEdit";
import OrganicPostCard from "../../../components/client/organic_social/OrganicPostCard";
import "./css/campaign.css";
import { campaigns } from "../client-tour/campaigns";
import useClientTour from "../../../hooks/useClientTour";

const Campaigns = () => {
  const [activeTab, setActiveTab] = useState("all_campaigns");
  const [show, setShow] = useState(false);
  const [campaignList, setCampaignList] = useState([]);
  const [isLoadingCampaign, setIsLoadingCampaign] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [campaign_id, setCampaignId] = useState();
  const [campaignsCount, setCampaignsCount] = useState(0);
  const [programList, setProgramList] = useState([]);
  const [filteredPrograms, setFilteredPrograms] = useState([]);
  const [selectedprogram, setSelectedProgram] = useState();
  const [organicPostsList, setOrganicPostsList] = useState([]);
  const loadingDelayRef = useRef(null);

  const user = useSelector((state) => state?.user);
  const filter = useSelector((state) => state?.filter);
  const tour = useSelector((state) => state?.tour);

  useClientTour({
    enabled: !user?.has_seen_tour && !tour?.campaigns_page && user?.is_client,
    steps: campaigns,
    tourKey: "campaigns_page",
    nextRoute: "/client/settings/users",
    skipRoute: "/client/campaigns",
  });

  // Fetch campaign list
  const getCampaignList = async () => {
    try {
      // Delay showing spinner to avoid flicker when response is fast
      loadingDelayRef.current = window.setTimeout(() => {
        setIsLoadingCampaign(true);
      }, 150);
      const method = "POST";
      const headers = {};
      const formData = new FormData();

      if (selectedprogram) {
        if (
          filter?.filter_ads_platform?.value &&
          filter?.filter_ads_platform?.value != "all"
        ) {
          formData.append("platform_id", filter?.filter_ads_platform?.value);
        }

        formData.append("program_id", selectedprogram);
        formData.append("user_id", user?.user_id);
        formData.append("role_id", user?.role_id);
        formData.append("status", filter?.filter_campaign_status?.value);
        formData.append("campaign_id", filter?.filter_campaign?.value || "");
        const startDate = filter?.filter_date_range?.startDate
          ? filter.filter_date_range.startDate.split("/").reverse().join("-")
          : "";

        const endDate = filter?.filter_date_range?.endDate
          ? filter.filter_date_range.endDate.split("/").reverse().join("-")
          : "";

        formData.append("start_date", startDate);
        formData.append("end_date", endDate);

        // if(filter?.filter_date_range?.days?.value!="Custom Range"){
        //   formData.append("days", filter?.filter_date_range?.days?.value || "");
        // }
        if (filter?.filter_date_range?.value != "Custom Range") {
          formData.append("days", filter?.filter_date_range?.value || "");
        }
      }

      const result = await handleApi(
        "campaign_list",
        method,
        headers,
        formData,
      );

      if (result?.success) {
        setCampaignList(result?.data?.internal_campaign_list);
        // setCampaignsCount(result?.data?.total_campaign_count);
        setOrganicPostsList(result?.data?.organic_posts);
      } else {
        toast.error(
          result?.error?.error_message || "Failed to fetch campaign list",
        );
      }
    } catch (e) {
      toast.error(e?.message || "Failed to fetch campaign list");
    } finally {
      if (loadingDelayRef.current) {
        clearTimeout(loadingDelayRef.current);
        loadingDelayRef.current = null;
      }
      setIsLoadingCampaign(false);
    }
  };

  useEffect(() => {
    if (selectedprogram) {
      getCampaignList();
    }
  }, [
    selectedprogram,
    filter?.filter_campaign_status,
    filter?.filter_date_range,
    filter?.filter_campaign,
    filter?.filter_ads_platform,
    show,
  ]);

  const getProgramList = async () => {
    try {
      setIsLoading(true);
      let method = "POST";
      let headers = {};
      let formData = new FormData();
      if (
        filter?.filter_ads_platform?.value &&
        filter?.filter_ads_platform?.value != "all"
      ) {
        formData.append("platform_id", filter?.filter_ads_platform?.value);
      }
      formData.append("client_id", user?.client_id);
      formData.append("role_id", user?.role_id);
      formData.append("user_id", user?.user_id);

      let result = await handleApi("program_list", method, headers, formData);
      if (result?.success) {
        let program_list = result?.data?.programs_list.sort(
          (a, b) => b.campaign_count - a.campaign_count,
        );
        setProgramList(
          result?.data?.programs_list.sort(
            (a, b) => b.campaign_count - a.campaign_count,
          ),
        );
        const totalCampaignCount = result?.data?.programs_list.reduce(
          (acc, program) => acc + program.campaign_count,
          0,
        );
        setCampaignsCount(totalCampaignCount);
        // Don't auto-open first accordion on load to avoid flicker when entering tab
      } else {
        // toast.error(
        //   result?.error?.error_message
        //     ? result?.error?.error_message
        //     : "Failed to fetch program list"
        // );
      }
    } catch (e) {
      console.log(e);
      // toast.error(e?.message ? e?.message : "Failed to fetch program list");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getProgramList();
  }, [user?.client_id]);

  useEffect(() => {
    if (user?.program_id) {
      const filteredProgram = programList?.find(
        (program) => program.program_id === user?.program_id,
      );
      if (filteredProgram) {
        setFilteredPrograms([filteredProgram]); // Ensure programList is an array
      }
    } else {
      setFilteredPrograms(programList);
    }
  }, [user?.program_id, programList]);

  useEffect(() => {
    getProgramList();
  }, [filter?.filter_ads_platform, show]);


  // Campaign tab switcher component
  const CampaignTabs = () => {
    return (
      <div className="row campaign_header_tab d-flex align-items-center mb-3 flex-wrap">
        <div className="col-lg-10 col-md-12 p-0">
          <div className="d-flex gap-3 py-2">
            <span onClick={() => setActiveTab("all_campaigns")}>
              <p
                className={`mb-0 ${
                  activeTab === "all_campaigns" && "campaign_header_tab_active"
                }`}
              >
                All Campaigns ({campaignsCount})
              </p>
            </span>
            {/* <span onClick={() => setActiveTab("mine_campaigns")}>
              <p
                className={`mb-0 ${
                  activeTab === "mine_campaigns" && "campaign_header_tab_active"
                }`}
              >
                Mine (0)
              </p>
            </span>
            <span onClick={() => setActiveTab("completed_campaigns")}>
              <p
                className={`mb-0 ${
                  activeTab === "completed_campaigns" &&
                  "campaign_header_tab_active"
                }`}
              >
                Completed (0)
              </p>
            </span>
            <span onClick={() => setActiveTab("archived_campaigns")}>
              <p
                className={`mb-0 ${
                  activeTab === "archived_campaigns" &&
                  "campaign_header_tab_active"
                }`}
              >
                Archived (0)
              </p>
            </span> */}
          </div>
        </div>
        <div className="col-lg-2 col-md-12 d-flex justify-content-end p-0">
          {(user?.role_id === 2 || user?.role_id === 1) && (
            <button
              onClick={() => setShow(true)}
              className="btn_primary create_campaign_btn"
            >
              Create Campaign
            </button>
          )}
        </div>
      </div>
    );
  };

  // Campaign list rendering
  const CampaignList = () => {
    const handleToggle = (programId) => {
      // Toggle: if clicking the same program, close it; otherwise, open the clicked one
      if (selectedprogram === programId) {
        setSelectedProgram(null);
      } else {
        setSelectedProgram(programId);
      }
    };

    return (
      <>
        <div className="custom-accordion campaign_list" id="accordionExample">
          {filteredPrograms.length > 0 ? (
            filteredPrograms.map((program) => (
              <div className="custom-accordion-item" key={program?.program_id}>
                <button
                  className={`custom-accordion-header ${
                    selectedprogram === program.program_id
                      ? "show"
                      : "collapsed"
                  }`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleToggle(program.program_id);
                  }}
                  type="button"
                  aria-expanded={
                    selectedprogram === program.program_id ? "true" : "false"
                  }
                  aria-controls={`collapse${program.program_id}`}
                >
                  <div className="d-flex justify-content-between w-100 me-3">
                    <span className="">{program?.program_name}</span>
                    <span className="small d-flex gap-3 align-items-center">
                      {program?.campaign_count || 0} ATTACHED CAMPAIGNS
                      <span
                        className={`toggle-arrow ${
                          selectedprogram === program.program_id ? "up" : "down"
                        }`}
                      ></span>
                    </span>
                  </div>
                </button>
                <div
                  id={`collapse${program.program_id}`}
                  className={`custom-accordion-collapse ${selectedprogram === program.program_id ? "show" : ""}`}
                  aria-labelledby={`heading${program.program_id}`}
                >
                  <div className="custom-accordion-body">
                    {isLoadingCampaign ? (
                      <div
                        className="d-flex align-items-center justify-content-center"
                        style={{ height: "100px" }}
                      >
                        <LoadingSpinner />
                      </div>
                    ) : campaignList.length > 0 ? (
                      campaignList.map((campaign_details, index) => (
                        <CampaignCard
                          key={campaign_details?.campaign_id || index}
                          campaign={campaign_details}
                          setCampaignId={setCampaignId}
                          setShow={setShow}
                          getCampaignList={getCampaignList}
                        />
                      ))
                    ) : (
                      <div
                        className="row campaign_sub_card d-flex align-items-center"
                        style={{ height: "100px" }}
                      >
                        <h5 className="text-center">No Campaigns found</h5>
                      </div>
                    )}

                    {/* <div className="px-3 py-2 my-3 d-none " >
                      <div className="organic_post_card_wrapper organic_posts_list" >
                        <div className=" d-flex  align-items-center organic_post_card"
                          style={{ height: "50px" }}>
                          <p className="mb-2 px-4 organic_post_heading">Organic Posts</p>
                        </div>

                        <div className=" organic_post_card_body" >
                          {organicPostsList?.length > 0 && organicPostsList?.map((post,index) => {

                            return <div className="py-1 px-4 my-2" key={index}>
                              <OrganicPostCard postDetails={post}

                                getCampaignList={getCampaignList}
                              />
                            </div>
                          })}
                          {organicPostsList?.length == 0 && !isLoadingCampaign && <div
                            className="row campaign_sub_card d-flex align-items-center"
                            style={{ height: "100px" }}
                          >
                            <h5 className="text-center">No Posts Found</h5>
                          </div>}
                        
                          {isLoadingCampaign && (
                            <div
                              className="d-flex align-items-center justify-content-center"
                              style={{ height: "100px" }}
                            >
                              <LoadingSpinner />
                            </div>
                          )}

                        </div>

                      </div>


                    </div> */}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="position-absolute top-50 start-50 translate-middle">
              <h5>No Programs found</h5>
            </div>
          )}
        </div>
      </>
    );
  };

  return (
    <div className="campaigns">
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <>
          <CampaignTabs />
          <CampaignList />
        </>
      )}
      <CampaignViewEdit
        program={selectedprogram}
        campaign_id={campaign_id}
        setCampaignId={setCampaignId}
        show={show}
        setShow={setShow}
      />
    </div>
  );
};

export default Campaigns;
